import {
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ToastContainerProps } from 'react-toast-notifications';
import styled from 'styled-components';

import type { TranscriptionLanguage } from '@treams/treams-network';
import { ToastProvider, useToasts } from '@treams/treams-ui';

import { LiveTranscriptToast } from './LiveTranscriptToast';
import {
	LOCK_PREFIX,
	type StartResult,
	type StreamingState,
	type StreamingTarget,
	useStreamingTranscription,
} from './useStreamingTranscription';

const LOCK_POLL_INTERVAL_MS = 2_000;

/** Width of the Crisp chat bubble plus its margin, so the toast stays clear of it. */
const HELPDESK_BUBBLE_WIDTH_PX = 88;

const ToastCorner = styled.div<{ $hasToasts: boolean }>`
	position: fixed;
	bottom: 0;
	right: ${HELPDESK_BUBBLE_WIDTH_PX}px;
	box-sizing: border-box;
	max-height: 100%;
	max-width: calc(100% - ${HELPDESK_BUBBLE_WIDTH_PX}px);
	overflow: hidden;
	padding: 8px;
	pointer-events: ${(props) => (props.$hasToasts ? 'auto' : 'none')};
	z-index: 1000;
`;

/**
 * The library's bottom-right container, shifted left so the live transcript
 * does not sit behind the helpdesk bubble in the corner.
 * @param props Passed by react-toast-notifications.
 * @param props.hasToasts Whether any toast is showing; the empty corner lets clicks through.
 * @param props.children The toasts.
 */
function LiveToastContainer({ hasToasts, children }: ToastContainerProps) {
	return <ToastCorner $hasToasts={hasToasts}>{children}</ToastCorner>;
}

type StreamingTranscriptionContextValue = {
	state: StreamingState;
	start: (
		target: StreamingTarget,
		options: { language: TranscriptionLanguage; transcriptionID?: string | null },
	) => Promise<void>;
	resume: () => Promise<void>;
	restart: (
		target: StreamingTarget,
		options: { language: TranscriptionLanguage; transcriptionID: string },
	) => Promise<void>;
	pause: () => Promise<void>;
	finish: () => Promise<void>;
	/** Leaves a finish that keeps failing; unsaved text is kept. */
	stopFinishing: () => void;
	/** Stops before anything was said, without confirmation. */
	cancel: () => Promise<void>;
	/** Throws away this session's text after the user confirmed. */
	discard: () => Promise<void>;
	close: () => void;
	setLanguage: (language: TranscriptionLanguage) => void;
	bindTarget: (target: StreamingTarget) => void;
	isStreamingTarget: (targetID: string) => boolean;
};

const StreamingTranscriptionContext = createContext<StreamingTranscriptionContextValue | null>(
	null,
);

export function useStreamingTranscriptionContext() {
	const context = useContext(StreamingTranscriptionContext);

	if (!context) {
		throw new Error(
			'useStreamingTranscriptionContext must be used within StreamingTranscriptionProvider',
		);
	}

	return context;
}

/**
 * True while another tab holds the recording lock for this target.
 * @param targetID The conversation or note this recording belongs to.
 * @param enabled When false, the lock is not checked and the result stays false.
 */
export function useRecordingInAnotherTab(targetID: string, enabled: boolean): boolean {
	const { isStreamingTarget } = useStreamingTranscriptionContext();
	const [heldElsewhere, setHeldElsewhere] = useState(false);
	const recordingHere = isStreamingTarget(targetID);

	useEffect(() => {
		if (!enabled || recordingHere || !navigator.locks) {
			setHeldElsewhere(false);
			return;
		}

		let cancelled = false;

		const check = () => {
			navigator.locks
				.query()
				.then(({ held }) => {
					if (cancelled) return;
					setHeldElsewhere(!!held?.some((lock) => lock.name === `${LOCK_PREFIX}${targetID}`));
				})
				.catch(() => {});
		};

		check();
		const interval = setInterval(check, LOCK_POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [enabled, recordingHere, targetID]);

	return heldElsewhere;
}

export function StreamingTranscriptionProvider({ children }: PropsWithChildren) {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const { state, controller } = useStreamingTranscription();

	const reportStartResult = useCallback(
		(result: StartResult) => {
			if (result === 'microphone') {
				addToast(t('transcription.failedToAccessMicrophone'), {
					appearance: 'error',
					autoDismiss: true,
				});
			} else if (result === 'otherTab') {
				addToast(t('transcription.streaming.recordingInAnotherTab'), {
					appearance: 'warning',
					autoDismiss: true,
				});
			} else if (result === 'audio') {
				addToast(t('voiceMemo.error.message'), { appearance: 'error', autoDismiss: true });
			}
		},
		[addToast, t],
	);

	const sessionTargetID = state.session?.targetID;

	const actions = useMemo(
		() => ({
			start: async (
				target: StreamingTarget,
				options: { language: TranscriptionLanguage; transcriptionID?: string | null },
			) => reportStartResult(await controller.start(target, options)),
			resume: async () => reportStartResult(await controller.resume()),
			restart: async (
				target: StreamingTarget,
				options: { language: TranscriptionLanguage; transcriptionID: string },
			) => reportStartResult(await controller.restart(target, options)),
			pause: () => controller.pause(),
			finish: () => controller.finish(),
			stopFinishing: () => controller.stopFinishing(),
			cancel: () => controller.cancel(),
			discard: () => controller.discard(),
			close: () => controller.close(),
			setLanguage: (language: TranscriptionLanguage) => controller.setLanguage(language),
			bindTarget: (target: StreamingTarget) => controller.bindTarget(target),
		}),
		[controller, reportStartResult],
	);

	const isStreamingTarget = useCallback(
		(targetID: string) => sessionTargetID === targetID,
		[sessionTargetID],
	);

	const value = useMemo<StreamingTranscriptionContextValue>(
		() => ({ ...actions, state, isStreamingTarget }),
		[actions, state, isStreamingTarget],
	);

	return (
		<StreamingTranscriptionContext.Provider value={value}>
			<ToastProvider placement="bottom-right" components={{ ToastContainer: LiveToastContainer }}>
				<LiveTranscriptToast />
			</ToastProvider>
			{children}
		</StreamingTranscriptionContext.Provider>
	);
}
